import { useCallback, useMemo, useRef } from "react";
import { CircleMarker, Polygon, Polyline } from "react-leaflet";
import type { Layer, PathOptions } from "leaflet";

import { StackedGroup } from "./LayerStack";
import { LAYER_RANK } from "./layerStackOrder";
import { FEATURE_POINT_RADIUS, featurePathOptions, isFeatureShown } from "./mapFeatures";
import { useMapFeatures } from "./useMapFeatures";
import { useMapZoom } from "./useMapZoom";

type MapFeatureLayerProps = {
  sitePlan: boolean;
};

export const MapFeatureLayer: React.FC<MapFeatureLayerProps> = ({ sitePlan }) => {
  const { features } = useMapFeatures();
  const zoom = useMapZoom();
  const layers = useRef(new Map<number, Layer>());

  const styled = useMemo(() => {
    const byClass = new Map<string, PathOptions>();

    return features.map(feature => {
      let pathOptions = byClass.get(feature.featureClass);
      if (!pathOptions) {
        pathOptions = featurePathOptions(feature.featureClass);
        byClass.set(feature.featureClass, pathOptions);
      }

      return { feature, pathOptions };
    });
  }, [features]);

  const shown = useMemo(
    () => styled.filter(({ feature }) => isFeatureShown(feature, zoom, sitePlan)),
    [styled, zoom, sitePlan]);

  // Note: a feature shown again, like the site extent when Site plan is ticked, is added above the rest,
  // so the group is restacked in sort order rather than in the order its paths were added.
  const drawOrder = useCallback(
    () => shown.flatMap(({ feature }) => layers.current.get(feature.id) ?? []),
    [shown]);

  const track = (id: number) => (layer: Layer | null) => {
    if (layer) layers.current.set(id, layer);
    else layers.current.delete(id);
  };

  return (
    <StackedGroup rank={LAYER_RANK.features} version={shown} drawOrder={drawOrder}>
      {shown.map(({ feature, pathOptions }) => {
        const key = `feature-${feature.id}`;
        const { geometry } = feature;

        if (geometry.kind === "point")
          return <CircleMarker key={key} ref={track(feature.id)} center={geometry.position} radius={FEATURE_POINT_RADIUS} pathOptions={pathOptions} interactive={false} />;

        if (geometry.kind === "line")
          return <Polyline key={key} ref={track(feature.id)} positions={geometry.path} pathOptions={pathOptions} interactive={false} />;

        return <Polygon key={key} ref={track(feature.id)} positions={geometry.rings} pathOptions={pathOptions} interactive={false} />;
      })}
    </StackedGroup>
  );
};

export default MapFeatureLayer;
